import { ToastService } from '@app/ui/shared/toast/toast.service';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { RequestMethod } from '@core/enums/httpRequest/requestMethods.enum';
import { NgStorage } from '@core/enums/ngStorage/ngStorage.enum';
import { environment } from '@core/environments/environments';
import { StorageMap } from '@ngx-pwa/local-storage';
import { catchError, from, lastValueFrom, map, toArray } from 'rxjs';
import { StorageService } from '../ngStorage/storage.service';
import { UserSessionService } from '../user-session/user-session.service';
export interface ApiResponse<T> {
  data: T;
}
@Injectable({
  providedIn: 'root',
})
export class HttpRequestService extends StorageService {
  public apiUrl = `${environment.baseUrl}`;
  constructor(
    public _http: HttpClient,
    public override _storage: StorageMap,
    public _router: Router,
    public _notify: ToastService,
    public _userSessionService: UserSessionService,
    @Inject(PLATFORM_ID) public platformId: object,
  ) {
    super(_storage, platformId);
  }

  public headers: HttpHeaders | null = null;

  /**
   * Returns HTTP headers with the authorization token set if the token exists.
   * Returns an empty HTTP headers object otherwise.
   * @returns Promise of the HTTP headers object
   */
  public async getAuthHeaders(): Promise<HttpHeaders> {
    const token = (await this.getStorage(NgStorage.TOKEN)) as string;

    if (token) {
      return new HttpHeaders().set('Authorization', `Bearer ${token}`);
    }
    return new HttpHeaders();
  }

  /**
   * Makes a request to the given route with the given payload and method.
   * @param route - URL of the request
   * @param payload - Payload of the request
   * @param method - HTTP method of the request
   * @returns Promise of the request response
   */
  public async makeRequest<T, P>(
    route: string,
    payload: P,
    method: string = RequestMethod.GET,
  ): Promise<T> {
    if (this._userSessionService.isSessionInvalidated) {
      throw new Error('Session invalidated');
    }
    return lastValueFrom(
      from(this.makeHttpRequest<ApiResponse<T>>(route, payload, method)).pipe(
        map((res: ApiResponse<T>) => res.data),
        catchError(async (error: HttpErrorResponse) => {
          console.error('Error:', error);
          await this.closeSessionIfHaveAuthError(error);
          throw error;
        }),
      ),
    );
  }

  /**
   * Makes a request to the given route with the given payload and method.
   * @param url - URL of the request
   * @param options - Payload of the request
   * @param method - HTTP method of the request
   * @returns Promise of the request response
   */
  async makeHttpRequest<T>(
    url: string,
    options: unknown = {},
    method: string = RequestMethod.GET,
  ): Promise<T> {
    const headers = await this.getAuthHeaders();
    // La comparación va normalizada porque el método llega de dos formas: el enum
    // (`RequestMethod.GET` vale 'Get') y el literal 'GET', que es como lo pasa la mayoría de
    // los servicios. Si no coinciden, la petición se arma con `body` y `fetch` la rechaza:
    // "Request with GET/HEAD method cannot have body". Con el backend XHR anterior el body
    // se ignoraba en silencio, así que el error apareció recién al activar `withFetch()`.
    const normalizedMethod = method.toUpperCase();
    const sendsBody = normalizedMethod !== 'GET' && normalizedMethod !== 'HEAD';
    const requestOptions: object = sendsBody ? { body: options, headers } : { headers };
    return lastValueFrom(
      this._http.request<T>(method, url, requestOptions).pipe(map((response) => response as T)),
    );
  }

  /**
   * Checks if the given error is a 401 or 403 status code (unauthorized or forbidden).
   * If so, clears the user session and shows a notification to the user to login again.
   * @param {HttpErrorResponse} error
   * @returns {Promise<void>}
   */
  public async closeSessionIfHaveAuthError(error: HttpErrorResponse): Promise<void> {
    if (error.status === 401) {
      if (this._userSessionService.isSessionInvalidated) return;
      this._userSessionService.isSessionInvalidated = true;
      await this.clearSession();
      this._notify.danger('Tu sesión ha expirado, por favor vuelva a iniciar sesión');
      this._router.navigate(['/login']);
    }
    if (error.status === 500) {
      this._router.navigate(['/500']);
    }
  }

  /**
   * Clears all user related data from local storage.
   * @returns {Promise<void>} - promise that resolves when all user related data is cleared from local storage
   */
  public async clearSession(): Promise<void> {
    const prefix = `${environment.appName}-`;
    const keys = await lastValueFrom(this._storage.keys().pipe(toArray()));
    await Promise.all(
      keys
        .filter((key) => key.startsWith(prefix))
        .map((key) => lastValueFrom(this._storage.delete(key))),
    );
  }
}
